import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  actionsClassName,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
  actionsClassName?: string;
}) {
  return (
    <header className="flex flex-col gap-5 border-b border-border pb-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="max-w-2xl">
        {eyebrow && <p className="mb-2 text-xs font-bold text-muted-foreground">{eyebrow}</p>}
        <h1 className="font-display m-0 text-3xl font-bold tracking-[-0.04em] text-foreground sm:text-[2.15rem] sm:leading-[1.12]">{title}</h1>
        <p className="mt-2 max-w-xl text-sm leading-6 text-muted-foreground sm:text-[15px]">{description}</p>
      </div>
      {actions && <div className={cn("flex shrink-0 flex-wrap items-center gap-2", actionsClassName)}>{actions}</div>}
    </header>
  );
}

export function DashboardLoading() {
  return (
    <div className="grid gap-8" aria-label="Loading account snapshot" aria-busy="true">
      <div className="grid gap-3 border-b border-border pb-6">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-5 w-full max-w-xl" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
      </div>
      <Skeleton className="h-72" />
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <section className="mx-auto grid max-w-lg justify-items-center gap-4 py-16 text-center" role="alert">
      <span className="grid size-12 place-items-center rounded-full bg-coral-soft text-destructive">
        <AlertTriangle className="size-5" />
      </span>
      <div>
        <h2 className="font-display text-xl font-bold tracking-[-0.02em]">Account data is unavailable</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{message}</p>
      </div>
      <Button variant="outline" onClick={onRetry}>
        <RefreshCw data-icon="inline-start" />
        Try again
      </Button>
    </section>
  );
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <section className="grid justify-items-center gap-3 rounded-2xl border border-dashed border-border bg-card/55 px-6 py-12 text-center">
      <span className="grid size-11 place-items-center rounded-full bg-muted text-muted-foreground">
        <Inbox className="size-5" />
      </span>
      <div className="max-w-md">
        <h2 className="font-display text-lg font-bold tracking-[-0.02em]">{title}</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
      {action}
    </section>
  );
}
